import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { journeyStoryApi } from '../../api/journeyStoryApi';
import { ConfirmDialog } from '../../components/ui';
import { useTravelText } from '../../app/TravelLocaleContext';
import { formatPlainDate } from '../../i18n/travelFormat';
import {
  memoryTypeLabelKey,
  normalizeMyConsentPage,
  storyErrorKind,
  storyErrorMessageKey,
} from '../journey-publication/journeyStoryModel';
import './my-publication-consents.css';

const PAGE_SIZE = 20;
const S = 'travel.publication.story';
const M = `${S}.mine`;

function consentTextKey(item) {
  if (item.authorConsent === 'GRANTED') {
    return item.ownerSelected ? `${M}.state.grantedSelected` : `${M}.state.grantedNotSelected`;
  }
  if (item.authorConsent === 'REVOKED') return `${M}.state.revoked`;
  return `${M}.state.pending`;
}

function loadErrorKey(kind) {
  if (kind === 'unauthorized') return `${M}.error.load.unauthorized`;
  if (kind === 'forbidden') return `${M}.error.load.forbidden`;
  if (kind === 'network') return `${S}.error.network`;
  return `${M}.error.load.generic`;
}

function revokeErrorKey(kind) {
  if (kind === 'forbidden') return `${M}.error.revoke.forbidden`;
  if (kind === 'not-found') return `${M}.error.revoke.notFound`;
  return storyErrorMessageKey(kind, { context: 'consent' });
}

/**
 * 「我的 → 公開故事授權」：跨行程查看本人回憶的收錄授權並撤回。
 * 不需讀取行程詳情、不要求仍為行程成員；此頁不提供新授權。
 */
export default function MyPublicationConsentsPage() {
  const { t, tf, intlLocale } = useTravelText();
  const generation = useRef(0);
  const busyRef = useRef(false);
  const [phase, setPhase] = useState('loading');
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [confirmItem, setConfirmItem] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const loadFirst = useCallback(async () => {
    const requestId = ++generation.current;
    try {
      const page = normalizeMyConsentPage(await journeyStoryApi.listMine({ limit: PAGE_SIZE }));
      if (requestId !== generation.current) return;
      setItems(page.items);
      setNextCursor(page.nextCursorItemId);
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      setError(loadErrorKey(storyErrorKind(cause)));
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    loadFirst();
    return () => {
      generation.current += 1;
    };
  }, [loadFirst]);

  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    const requestId = generation.current;
    setLoadingMore(true);
    setError('');
    try {
      const page = normalizeMyConsentPage(await journeyStoryApi.listMine({ cursorItemId: nextCursor, limit: PAGE_SIZE }));
      if (requestId !== generation.current) return;
      setItems((current) => {
        const seen = new Set(current.map((item) => item.memoryEntryId));
        return [...current, ...page.items.filter((item) => !seen.has(item.memoryEntryId))];
      });
      setNextCursor(page.nextCursorItemId);
    } catch (cause) {
      if (requestId !== generation.current) return;
      setError(loadErrorKey(storyErrorKind(cause)));
    } finally {
      if (requestId === generation.current) setLoadingMore(false);
    }
  }

  async function revoke() {
    const item = confirmItem;
    if (!item || busyRef.current) return;
    busyRef.current = true;
    setRevoking(true);
    setError('');
    setFeedback('');
    try {
      await journeyStoryApi.setConsent(item.journeyId, item.memoryEntryId, false);
      setFeedback(`${S}.consent.revokedFeedback`);
    } catch (cause) {
      setError(revokeErrorKey(storyErrorKind(cause)));
    } finally {
      busyRef.current = false;
      setRevoking(false);
      setConfirmItem(null);
      await loadFirst();
    }
  }

  return (
    <main className="my-consents" data-testid="my-publication-consents">
      <p className="my-consents__back"><Link to="/me">{t(`${M}.back`)}</Link></p>
      <h1 className="my-consents__title">{t(`${M}.title`)}</h1>
      <p className="my-consents__hint">
        {t(`${M}.intro`)}
      </p>
      {phase === 'loading' ? <p className="my-consents__hint" aria-live="polite">{t(`${M}.loading`)}</p> : null}
      {error ? <p className="my-consents__error" role="alert">{t(error)}</p> : null}
      {feedback ? <p className="my-consents__status" role="status">{t(feedback)}</p> : null}
      {phase === 'error' ? (
        <button type="button" className="my-consents__button" onClick={() => { setError(''); setPhase('loading'); loadFirst(); }}>
          {t(`${M}.reload`)}
        </button>
      ) : null}
      {phase === 'ready' && !items.length ? (
        <p className="my-consents__hint" data-testid="my-consents-empty">{t(`${M}.empty`)}</p>
      ) : null}
      {phase === 'ready' && items.length ? (
        <ul className="my-consents__list">
          {items.map((item) => {
            const title = item.memoryTitle || t(`${S}.untitled`);
            const occurredDate = formatPlainDate(item.occurredAt, intlLocale);
            return (
              <li
                key={`${item.journeyId}-${item.memoryEntryId}`}
                className="my-consents__item"
                data-memory-entry-id={item.memoryEntryId}
                data-consent-state={item.authorConsent}
              >
                <strong className="my-consents__item-title">{title}</strong>
                <p className="my-consents__meta">
                  <span>{t(memoryTypeLabelKey(item.memoryType))}</span>
                  {occurredDate ? <time dateTime={String(item.occurredAt).slice(0, 10)}>{occurredDate}</time> : null}
                  {item.memoryStatus === 'HIDDEN' ? <span>{t(`${M}.hidden`)}</span> : null}
                </p>
                <p className="my-consents__meta">
                  {item.publicJourneyTitle && item.publicationId ? (
                    <Link to={`/public/journeys/${item.publicationId}`}>{item.publicJourneyTitle}</Link>
                  ) : (
                    <span>{t(`${M}.notPublic`)}</span>
                  )}
                </p>
                <p className="my-consents__state">{t(consentTextKey(item))}</p>
                {item.authorConsent === 'GRANTED' ? (
                  <button
                    type="button"
                    className="my-consents__button my-consents__button--revoke"
                    aria-label={tf(`${M}.revokeLabel`, { title })}
                    disabled={revoking}
                    onClick={() => setConfirmItem(item)}
                  >
                    {t(`${M}.revoke`)}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {phase === 'ready' && nextCursor ? (
        <button type="button" className="my-consents__button" disabled={loadingMore} onClick={loadMore}>
          {t(loadingMore ? `${M}.loadingMore` : `${M}.loadMore`)}
        </button>
      ) : null}
      <ConfirmDialog
        open={Boolean(confirmItem)}
        title={t(`${M}.confirmTitle`)}
        description={t(`${M}.confirmBody`)}
        confirmLabel={t(`${M}.revoke`)}
        cancelLabel={t(`${M}.cancel`)}
        danger
        loading={revoking}
        onConfirm={revoke}
        onCancel={() => { if (!busyRef.current) setConfirmItem(null); }}
      />
    </main>
  );
}
